import { Pencil, Trash2, Users } from 'lucide-react';
import type { NotificationGroup, RoutingPerson } from '../../api/notificationRouting';
import { peopleCount } from './routingView';

export interface NotificationGroupCardProps {
  group: NotificationGroup;
  members: RoutingPerson[];
  /** The notifications it is chosen for, by label. */
  usedFor: string[];
  /** "Office-wide" on an event's page, where both kinds are listed. */
  badge?: string;
  onEdit?: () => void;
  onDelete?: () => void;
}

const SHOWN_MEMBERS = 6;

/** A group as a card: who is in it, what it is chosen for, and its actions. */
export function NotificationGroupCard({
  group,
  members,
  usedFor,
  badge,
  onEdit,
  onDelete,
}: NotificationGroupCardProps) {
  const extra = members.length - SHOWN_MEMBERS;
  return (
    <article
      aria-label={group.name}
      className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4"
    >
      <header className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600">
          <Users size={18} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="truncate text-sm font-semibold text-gray-900">{group.name}</h4>
            {badge && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">
                {badge}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500">
            {group.description ? `${group.description} · ` : ''}
            {peopleCount(members.length)}
          </p>
        </div>
      </header>

      {members.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label="Members">
          {members.slice(0, SHOWN_MEMBERS).map((person) => (
            <li
              key={person.id}
              className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700"
            >
              {person.name}
              {person.isClient && (
                <span className="rounded-full bg-amber-100 px-1.5 text-[10px] font-medium text-amber-800">
                  Client
                </span>
              )}
            </li>
          ))}
          {extra > 0 && (
            <li className="rounded-full px-2 py-1 text-xs text-gray-500">+{extra} more</li>
          )}
        </ul>
      )}

      <p className="text-xs text-gray-500">
        {usedFor.length > 0 ? (
          <>
            <span className="font-medium text-gray-700">Gets:</span> {usedFor.join(', ')}
          </>
        ) : (
          'Not chosen for any notification yet'
        )}
      </p>

      {(onEdit || onDelete) && (
        <div className="mt-auto flex gap-2 border-t border-gray-100 pt-3">
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-primary-700 hover:bg-primary-50 sm:min-h-9"
            >
              <Pencil size={14} aria-hidden="true" />
              Edit
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-red-600 hover:bg-red-50 sm:min-h-9"
            >
              <Trash2 size={14} aria-hidden="true" />
              Delete
            </button>
          )}
        </div>
      )}
    </article>
  );
}
